import React, { Component, Fragment } from "react";
import {
  Dropdown,
  Button,
  DropdownOption,
  Popup,
  WithPopup,
  Datagrid,
  DatagridRow,
  DatagridColumnDef,
  Checkbox,
} from "@abb/abb-common-ux-react";
import { ChartTypes, ISeries, ITrendLabels, ITrendProps } from "./Trend.types";
import { isNullOrUndefined, getLabelByKey } from "./Trend.utils";
import { TrendGraph } from "./TrendGraph";
import { TrendDateRangePicker } from "./TrendDateRangePicker";
import enLocale from "d3-time-format/locale/en-US.json";

import "./Trend.scss";
import "@abb/abb-common-ux-react/styles.css";

interface IState {
  activeRowId: string;
  popupOpen: boolean;
  isAnyCheckboxSelected: boolean;
  checkboxStates: any; // State to track selected checkboxes for each row
  limits: any;
}

export class Trend extends Component<ITrendProps, IState> {  
  groupedData: DatagridRow[][] = [];

  // Default Props
  defaultLocaleText: ITrendLabels = {
    area: "Area",
    average: "Average",
    bar: "Bar",
    circle: "Circle",
    color: "Color",
    chartType: "Chart Type",
    day: "Day",
    deviation: "Deviation",
    fillType: "Fill Type",
    gradient: "Gradient",
    hour: "Hour",
    invalidPointType: "Invalid Point Type",
    line: "Line",
    markerType: "Marker Type",
    maxValue: "Max Value",
    millisecond: "Millisecond",
    minute: "Minute",
    minValue: "Min Value",
    month: "Month",
    noDisplayLimits: "No Display Limits",
    none: "None",
    property: "Property",
    scatterPlot: "Scatter Plot",
    second: "Second",
    solid: "Solid",
    square: "Square",
    staircaseEnd: "Staircase-End",
    staircaseStart: "Staircase-Start",
    type1: "Type1",
    type2: "Type2",
    type3: "Type3",
    unit: "Unit",
    week: "Week",
    year: "Year",
    AlarmLow1: "Alarm Low1",
    AlarmHigh1: "Alarm High1",
    AlarmLow2: "Alarm Low2",
    AlarmHigh2:"Alarm High2",
  };

  public static defaultProps: Partial<ITrendProps> = {
    showTooltipOnPos: false,
    chartType: "Line",
    showChartType: true,
    chartTypeCollection: [
      "Line",
      "Area",
      "Bar",
      "ScatterPlot",
      "Staircase-Start",
      "Staircase-End",
    ],
    series: [] as ISeries[],
    trendChartHeight: 350,
    showHorizontalGridLines: true,
    showVerticalGridLines: true,
    showTableData: true,
    gridLineColor: "#bababa",
    gridLineWidth: 0.5,
    yAxisTicksCount: 6,
    xAxisTicksCount: 11,
    gridColumns: [
      "Color",
      "Name",
      "Unit",
      "MinValue",
      "MaxValue",
      "Average",
      "Deviation",
    ],
    showDataLimitConfigColumn: true,
    showConfigMenuColumn: true,
    showOuterBorder: true,
    showDateTimePicker: true,
    xAxis: {
      type: "DateTime",
      start: new Date(),
      end: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
      widthPerTicks: 60,
      tickAngle: 0,
      height: 50,
    },
    yAxis: { min: 10, max: 100, decimalPlaces: 2 },
    isMultipleYAxis: true,
    xAxisLabelFormat: "auto",
    markerStyle: {
      type: "Circle",
      fillColor: "white",
      strokeColor: "SeriesColor",
      strokeWidth: 1.5,
      radius: 4,
      width: 6,
      height: 8,
    },
    invalidStatuses: [] as string[],
    invalidPointLineType: "Type1",
    onChartTypeChange: () => "Line",
    width: "100%",
    yAxisWidth: 30,
    tooltipPosition: "Left",
    tooltipFormat: null,
    d3Locale: enLocale,
    showCommonXLabel: true,
    maxDate: new Date(9999, 11, 31, 23, 59, 59, 999),
    minDate: new Date(2010, 0, 1, 1, 1, 1, 1),
    datePickerFormat: "M/d/yyy H:mm:s:SSS",
    datePickerOptions: {},
    showUclLclToggle: false,
    isMultiSelect: false,
    showUclLcl: false,
    uclLcl: [],
    syncTime: false,
    showFutureValue: false,
  };

  columns: DatagridColumnDef[];
  showActiveDataLimitEyeIcon = this.props.showActiveDataLimitEyeIcon !== undefined && this.props.showActiveDataLimitEyeIcon === true  ? true : false;
  showDataLimitTitle= this.props.showDataLimitTitle !== undefined && this.props.showDataLimitTitle === true  ? true : false;        
  showSeparatorInTrendTable= this.props.showSeparatorInTrendTable !== undefined && this.props.showSeparatorInTrendTable === true  ? true : false;
  customizedMultipleYAxis= this.props.customizedMultipleYAxis !== undefined && this.props.customizedMultipleYAxis === true  ? true : false;
  removeDataLimitsFromTooltip= this.props.removeDataLimitsFromTooltip !== undefined && this.props.removeDataLimitsFromTooltip === true  ? true : false;

  mergedLocaleText: ITrendLabels;
  constructor(props: ITrendProps) {
    super(props);
    this.mergedLocaleText = { ...this.defaultLocaleText, ...props.labelTexts };
    this.columns = this.composeGridColumns();
    this.state = {
      activeRowId: null,
      popupOpen: false,
      isAnyCheckboxSelected: false,
      checkboxStates: {},
      limits: {},
    };    
  }

 
  /**
   * Method to populate Grid columns
   */
  composeGridColumns = () => {
    let columns: DatagridColumnDef[] = this.props.gridColumns.map((col) => {
      return this.getColumnDefByKey(col);
    });

    if (this.props.showDataLimitConfigColumn) {
      columns.push.apply(columns, [
        {
          fieldKey: "displayLimitMenu",
          title: "",
          align: "right",
          width: 80,
        },
      ]);
    }

    if (this.props.showConfigMenuColumn) {
      columns.push.apply(columns, [
        {
          fieldKey: "configMenu",
          title: "",
          align: "right",
          width: 80,
        },
      ]);
    }
    return columns;
  };

  /**
   * Get data grid rows based on selected properties
   */
  composeGridRow = (series: ISeries[]) => {
    let renderedSeries: ISeries[] = series.filter(
      (item) => item.isShownInChart
    );
    let rows: DatagridRow[] = renderedSeries.map((item, index) => {
      return {
        rowIndexId: index,
        fields: this.getRowFieldsObject(item, index),
      };
    });
    return rows;
  };

  /**
   * Method to populate the square color div which is show in legend grid
   */
  getColorContent = (color: string) => {
    return (
      <div
        className="trend-color-square"
        style={{
          width: "20px",
          height: "20px",
          backgroundColor: color,
          borderRadius: "2px",
        }}
      ></div>
    );
  };

  /**
   * Event Handler method for chart type dropdown
   * @param value Selected value in chart type dropdown
   */
  onChartTypeChange = (value: any) => {
    this.props.onChartTypeChange(value[0].value);
  };

  /**
   * Get column definitions to show in legend grid
   */
  getColumnDefByKey = (key: string) => {
    let cols: any = [
      {
        fieldKey: "color",
        title: "",
        width: 50,
      },
      {
        fieldKey: "name",
        title: getLabelByKey("property", this.mergedLocaleText),
        width: this.showActiveDataLimitEyeIcon ? 100 : 250,
      },
      {
        fieldKey: "unit",
        title: getLabelByKey("unit", this.mergedLocaleText),
        width: 100,
      },
      {
        fieldKey: "minValue",
        title: getLabelByKey("minValue", this.mergedLocaleText),
        align: "center",
      },
      {
        fieldKey: "maxValue",
        title: getLabelByKey("maxValue", this.mergedLocaleText),
        align: "center",
      },
      {
        fieldKey: "average",
        title: getLabelByKey("average", this.mergedLocaleText),
        align: "center",
      },
      {
        fieldKey: "deviation",
        title: getLabelByKey("deviation", this.mergedLocaleText),
        align: "center",
      },
      {
        fieldKey: "AlarmLow1",
        title: getLabelByKey("AlarmLow1", this.mergedLocaleText),
        align: "center",
      },
      {
        fieldKey: "AlarmHigh1",
        title: getLabelByKey("AlarmHigh1", this.mergedLocaleText),
        align: "center",
      },
      {
        fieldKey: "AlarmLow2",
        title: getLabelByKey("AlarmLow2", this.mergedLocaleText),
        align: "center",
      },
      {
        fieldKey: "AlarmHigh2",
        title: getLabelByKey("AlarmHigh2", this.mergedLocaleText),
        align: "center",
      },
    ];

    let column = cols.find((col: any) => {
      return col.fieldKey.toLowerCase() === key.toLowerCase();
    });
    return column
      ? column
      : {
          fieldKey: key,
          title: key,
        };
  };

  /** Get configuration menu popup in data grid  */
  getConfigMenuPopup = (item: ISeries) => {
    return (
      <WithPopup>
        <div style={{ display: "inline" }}>
          <Button sizeClass="small" icon="abb/menu-narrow" />
        </div>
        <Popup trigger="click" position="left center">
          <div className="trend-config-popup">
            <div>
              <p className="color-text">
                {getLabelByKey("color", this.mergedLocaleText)}
              </p>
              <label
                className="color-label"
                style={{ backgroundColor: item.color }}
              >
                <input
                  type="color"
                  className="color-input"
                  onChange={(e: any) => {
                    this.onSeriesColorChange(e.target.value, item);
                  }}
                  value={item.color}
                ></input>
              </label>
            </div>
            <Dropdown
              label={getLabelByKey("chartType", this.mergedLocaleText)}
              className="config-dd"
              value={[
                {
                  value: item.chartType || this.props.chartType,
                  label: getLabelByKey(
                    item.chartType || this.props.chartType,
                    this.mergedLocaleText
                  ),
                },
              ]}
              onChange={(value: any[]) => {
                this.seriesChartTypeChange(value, item);
              }}
            >
              {this.props.chartTypeCollection.map(
                (item: ChartTypes, index: number) => (
                  <DropdownOption
                    key={index}
                    label={getLabelByKey(item, this.mergedLocaleText)}
                    value={item}
                  />
                )
              )}
            </Dropdown>
            {((isNullOrUndefined(item.chartType) &&
              this.props.chartType === "Area") ||
              item.chartType === "Area") && (
              <Dropdown
                label={getLabelByKey("fillType", this.mergedLocaleText)}
                className="config-dd"
                value={[
                  {
                    value: item.fillType || "Solid",
                    label: getLabelByKey(
                      item.fillType || "Solid",
                      this.mergedLocaleText
                    ),
                  },
                ]}
                onChange={(value: any[]) => {
                  this.seriesFillTypeChange(value, item);
                }}
              >
                <DropdownOption
                  label={getLabelByKey("Solid", this.mergedLocaleText)}
                  value="Solid"
                />
                <DropdownOption
                  label={getLabelByKey("Gradient", this.mergedLocaleText)}
                  value="Gradient"
                />
              </Dropdown>
            )}
            <Dropdown
              label={getLabelByKey("markerType", this.mergedLocaleText)}
              className="config-dd"
              value={[
                {
                  value: item.markerStyle?.type || this.props.markerStyle?.type,
                  label: getLabelByKey(
                    item.markerStyle?.type || this.props.markerStyle?.type,
                    this.mergedLocaleText
                  ),
                },
              ]}
              onChange={(value: any[]) => {
                this.seriesMarkerChange(value, item);
              }}
            >
              <DropdownOption
                label={getLabelByKey("None", this.mergedLocaleText)}
                value="None"
              />
              <DropdownOption
                label={getLabelByKey("Circle", this.mergedLocaleText)}
                value="Circle"
              />
              <DropdownOption
                label={getLabelByKey("Square", this.mergedLocaleText)}
                value="Square"
              />
            </Dropdown>
            <Dropdown
              label={getLabelByKey("invalidPointType", this.mergedLocaleText)}
              className="config-dd"
              value={[
                {
                  value:
                    item.invalidPointLineType ||
                    this.props.invalidPointLineType,
                  label: getLabelByKey(
                    item.invalidPointLineType ||
                      this.props.invalidPointLineType,
                    this.mergedLocaleText
                  ),
                },
              ]}
              onChange={(value: any[]) => {
                this.onInvalidPointChange(value, item);
              }}
            >
              <DropdownOption
                label={getLabelByKey("None", this.mergedLocaleText)}
                value="None"
              />
              <DropdownOption
                label={getLabelByKey("Type1", this.mergedLocaleText)}
                value="Type1"
              />
              <DropdownOption
                label={getLabelByKey("Type2", this.mergedLocaleText)}
                value="Type2"
              />
              <DropdownOption
                label={getLabelByKey("Type3", this.mergedLocaleText)}
                value="Type3"
              />
            </Dropdown>
          </div>
        </Popup>
      </WithPopup>
    );
  };

  setOpacityOfEyeIcon = (item: any) => {
    this.setState(
      (prevState) => {
        return {
          activeRowId: prevState.activeRowId === item.id ? null : item.id,
          popupOpen: true,
        };
      },
      () => {
        this.props.setSeriesData();
      }
    );
  };

  /** Get Popup element for display limit popup */
  getDisplayLimitPopup = (item: ISeries, itemRowIndex: number) => {
    const { activeRowId, checkboxStates } = this.state;

    return (
      <Fragment>
        {this.showActiveDataLimitEyeIcon === true ? (
          <WithPopup>
            <div id={itemRowIndex?.toString()}>
              <Button
                sizeClass="small"
                icon="abb/view"
                style={{
                  opacity:
                    this.state.popupOpen == true &&
                    this.state.activeRowId === item.id
                      ? "1"
                      : "0.5",
                  color: "black",
                }}
                className={"displayLimitIcon" + item.id}
                id={itemRowIndex?.toString()}
                onClick={() => this.setOpacityOfEyeIcon(item)}
              />
            </div>

            <Popup
              trigger="controlled"
              position="left center"
              show={this.state.popupOpen && this.state.activeRowId === item.id}
              //onOpen={() => this.setOpacityOfEyeIcon(item.id)}
              //onClose={() => this.setOpacityOfEyeIcon(item.id)}
              id={itemRowIndex?.toString()}
            >
              {!isNullOrUndefined(item.dataLimits) &&
              item.dataLimits.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {item.dataLimits &&
                    item.dataLimits.length > 0 &&
                    item.dataLimits.map((limit: any, limitindex: number) => {
                      return (
                        <div
                          className={"data-limit-legend " + limit.className}
                          key={limitindex + itemRowIndex}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Checkbox
                            id={limitindex?.toString() + itemRowIndex?.toString()}
                            value={limit.shownInChart}
                            onChange={this.onDisplayLimitChange}
                            data={{ series: item, limit }}
                            sizeClass="small"
                          />
                          <div
                            className="limit-img"
                            style={{
                              display: "inline-block",
                              width: "30px",
                              height: "10px",
                              marginRight: "5px",
                            }}
                          ></div>
                          <div className="limit-label">{limit.name}</div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div>
                  <span>
                    {getLabelByKey("noDisplayLimits", this.mergedLocaleText)}
                  </span>
                </div>
              )}
            </Popup>
          </WithPopup>
        ) : (
          <WithPopup>
            <div>
              <Button sizeClass="small" icon="abb/view"  style={{
                  opacity: 0.5 }}/>
            </div>
            <Popup trigger="click" position="left center">
              {!isNullOrUndefined(item.dataLimits) &&
              item.dataLimits.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {item.dataLimits &&
                    item.dataLimits.length > 0 &&
                    item.dataLimits.map((limit: any, index: number) => {
                      return (
                        <div
                          className={"data-limit-legend " + limit.className}
                          key={index}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Checkbox
                            value={limit.shownInChart}
                            onChange={this.onDisplayLimitChange}
                            data={{ series: item, limit }}
                            sizeClass="small"
                          />
                          <div
                            className="limit-img"
                            style={{
                              display: "inline-block",
                              width: "30px",
                              height: "10px",
                              marginRight: "5px",
                            }}
                          ></div>
                          <div className="limit-label">{limit.name}</div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div>
                  <span>
                    {getLabelByKey("noDisplayLimits", this.mergedLocaleText)}
                  </span>
                </div>
              )}
            </Popup>
          </WithPopup>
        )}
      </Fragment>
    );
  };

  /**
   * Method to populate the row data for legend grid based on columns
   */
  getRowFieldsObject = (item: any, itemRowIndex: number) => {
    let obj: any = {};
    this.columns.forEach((col: DatagridColumnDef) => {
      if (col.fieldKey === "color") {
        obj[col.fieldKey] = this.getColorContent(item[col.fieldKey]);
      } else if (col.fieldKey === "displayLimitMenu") {
        obj[col.fieldKey] = this.getDisplayLimitPopup(item, itemRowIndex);
      } else if (col.fieldKey === "configMenu") {
        obj[col.fieldKey] = this.getConfigMenuPopup(item);
      } else if (col.fieldKey === "unit") {
        obj[col.fieldKey] = item[col.fieldKey] ? item[col.fieldKey] : "--";
      } else {
        const value: any = (obj[col.fieldKey] = isNullOrUndefined(
          item[col.fieldKey]
        )
          ? isNullOrUndefined(item.customFields)
            ? ""
            : item.customFields[col.fieldKey]
          : item[col.fieldKey]);
        if (this.props.cellFormatter && value !== "") {
          obj[col.fieldKey] = this.props.cellFormatter(value, col.fieldKey);
        }

        if (
          value !== "" &&
          !this.props.cellFormatter &&
          this.props.cellDecimalPlaces !== null &&
          this.props.cellDecimalPlaces !== undefined &&
          (col.fieldKey === "minValue" ||
            col.fieldKey === "maxValue" ||
            col.fieldKey === "average" ||
            col.fieldKey === "deviation")
        ) {
          obj[col.fieldKey] = value.toFixed
            ? value.toFixed(this.props.cellDecimalPlaces)
            : value;
        }
      }
    });
    return obj;
  };

  /**
   * Method to handle the data limit series visibility change from legend grid
   */
  onDisplayLimitChange = (data: any) => {
    this.props.onDataLimitVisibilityChange &&
      this.props.onDataLimitVisibilityChange(
        data.series,
        data.limit,
        !data.limit.shownInChart
      );
  };

  // handleCheckboxChange = (
  //     limitIndex: number,
  //     series: ISeries,
  //     limit: any,
  //     itemRowIndex: any
  // ) => {
  //     const data = { series, limit };
  //     this.onDisplayLimitChange(data);

  //     this.setState((prevState) => {
  //         const { checkboxStates, limits, seriesData } = prevState;
  //         const newLimitState: Record<string, any[]> = {};

  //         const newRowState: Record<string, Record<number, boolean>> = {};

  //         // Toggle the checkbox state for the selected row and limit index
  //         newRowState[itemRowIndex] = {
  //             ...(checkboxStates[itemRowIndex] || {}),
  //             [limitIndex]: !checkboxStates[itemRowIndex]?.[limitIndex],
  //         };

  //         // Update limits for the current row only
  //         let updatedLimits = {};
  //         updatedLimits = {
  //             ...limits,
  //             [itemRowIndex]: [...(limits[itemRowIndex] || []), limit],
  //         };

  //         // Update limits for the current row only
  //         let updatedSeries = {
  //             ...seriesData,
  //             [itemRowIndex]: [...(seriesData[itemRowIndex] || []), series],
  //         };

  //         // Unselect checkboxes for other rows if they are not the active row
  //         Object.keys(checkboxStates).forEach((id) => {
  //             if (id !== itemRowIndex?.toString()) {
  //                 newRowState[id] = {};
  //                 updatedLimits = {};
  //             }
  //         });

  //         // Unselect limits for other rows if they are not the active row
  //         Object.keys(limits).forEach((id) => {
  //             if (id !== itemRowIndex?.toString()) {
  //                 // Loop through the updated limits for the current row
  //                 limits[id].forEach((limit: any) => {
  //                     limit.shownInChart = true;
  //                     const series = seriesData[id][0];

  //                     const data = { series, limit };
  //                     this.onDisplayLimitChange(data);
  //                 });
  //             }
  //         });

  //         return {
  //             checkboxStates: newRowState,
  //             limits: updatedLimits,
  //             seriesData: updatedSeries,
  //         };
  //     });
  // };

  /**
   * Method to handle the invalid point type change event from legend grid
   */
  onInvalidPointChange = (value: any[], item: ISeries) => {
    this.props.onInvalidPointTypeChange &&
      this.props.onInvalidPointTypeChange(value[0].value, item);
  };

  /**
   * Method to handle the color change action from legend grid
   */
  onSeriesColorChange = (value: string, item: ISeries) => {
    this.props.onSeriesColorChange &&
      this.props.onSeriesColorChange(value, item);
  };

  /**
   * Method to handle the series type change from legend grid
   */
  seriesChartTypeChange = (value: any[], item: ISeries) => {
    this.props.onSeriesChartTypeChange &&
      this.props.onSeriesChartTypeChange(value[0].value, item);
  };

  /**
   * Method to handle the series fill type change from legend grid
   */
  seriesFillTypeChange = (value: any[], item: ISeries) => {
    this.props.onSeriesFillTypeChange &&
      this.props.onSeriesFillTypeChange(value[0].value, item);
  };

  /**
   * Method to handle marker type change from legend grid
   */
  seriesMarkerChange = (value: any[], item: ISeries) => {
    this.props.onSeriesMarkerChange &&
      this.props.onSeriesMarkerChange(value[0].value, item);
  };
  handleToggle = () => {
    this.props.handleUclLclToggle();
  };
  handleUclLclChange = (value: any) => {
    let val = value.map((item: any) => item?.value);
    this.props.handleUclLclChange(val);
  };

  
// Function to split arrays per grade
groupArraysByField = (rowData: DatagridRow[]): DatagridRow[][] => {
  const groupedData: Record<string, DatagridRow[]> = rowData.reduce((groups, item) => {
      const valueToSplitOn = item?.fields?.["Grade/Product"]?.toString(); // Convert to string to use as key
      if (!groups[valueToSplitOn]) {
          groups[valueToSplitOn] = [];
      }
      groups[valueToSplitOn].push(item);
      return groups;
  }, {} as Record<string, DatagridRow[]>); // Add type annotation here

  // Convert groupedData object into an array of arrays
  const groupedArray: DatagridRow[][] = Object.values(groupedData);

  return groupedArray;
};

  render() {
    const rowData: DatagridRow[] = this.composeGridRow(this.props.series);
    this.groupedData = this.groupArraysByField(rowData);    
    return (
      <div
        className={
          "td-root" + (this.props.showOuterBorder ? " td-outer-border" : "")
        }
      >
        {(this.props.showChartType || this.props.showUclLclToggle) && (
          <div className="td-drop-down">
            {this.props.showChartType && (
              <Dropdown
                className="chart-type-dd"
                value={[
                  {
                    value: this.props.chartType,
                    label: getLabelByKey(
                      this.props.chartType,
                      this.mergedLocaleText
                    ),
                  },
                ]}
                onChange={this.onChartTypeChange}
              >
                {this.props.chartTypeCollection.map((value: string) => {
                  return (
                    <DropdownOption
                      key={value}
                      value={value}
                      label={getLabelByKey(value, this.mergedLocaleText)}
                    ></DropdownOption>
                  );
                })}
              </Dropdown>
            )}
            {this.props.showUclLclToggle && (
              <>
                <div className="button">
                  <button
                    type="button"
                    className="switch-ucl-lcl"
                    onClick={() => {
                      this.handleToggle();
                    }}
                    disabled={this.props.uclLcl.length === 0}
                  >
                    {this.props.showUclLcl ? "Hide" : "Show"} UCL-LCL
                  </button>
                </div>
                <Dropdown
                  className="ucl-lcl-type-dd"
                  style={{ width: this.props.uclLcl.length ? "auto" : "250px" }}
                  placeholder={"select property"}
                  value={this.props.uclLcl.map((ele) => {
                    return { value: ele, label: ele };
                  })}
                  onChange={this.handleUclLclChange}
                  multiselect={this.props.isMultiSelect}
                >
                  {this.props.series.map((item: any, index: number) => {
                    return (
                      <DropdownOption
                        key={index}
                        value={item?.name || item?.id}
                        label={item?.name || item?.id}
                      ></DropdownOption>
                    );
                  })}
                </Dropdown>
              </>
            )}
          </div>
        )}
        <div className="trend-container">
          <TrendGraph
            d3Locale={this.props.d3Locale}
            width={this.props.width}
            xAxis={{
              ...Trend.defaultProps.xAxis,
              ...this.props.xAxis,
            }}
            isMultipleYAxis={this.props.isMultipleYAxis}
            yAxis={this.props.yAxis}
            syncTime={this.props.syncTime}
            series={this.props.series}
            height={this.props.trendChartHeight}
            chartType={this.props.chartType}
            showHorizontalGridLines={this.props.showHorizontalGridLines}
            showVerticalGridLines={this.props.showVerticalGridLines}
            tooltipPosition={this.props.tooltipPosition}
            tooltipFormat={this.props.tooltipFormat}
            gridLineColor={this.props.gridLineColor}
            gridLineWidth={this.props.gridLineWidth}
            xAxisTicksCount={this.props.xAxisTicksCount}
            yAxisTicksCount={this.props.yAxisTicksCount}
            backgroundColor={this.props.backgroundColor}
            backgroundImage={this.props.backgroundImage}
            markerStyle={{
              ...Trend.defaultProps.markerStyle,
              ...this.props.markerStyle,
            }}
            xAxisLabelFormat={this.props.xAxisLabelFormat}
            onDateRangeChange={this.props.onDateRangeChange}
            dateUnitCount={this.props.dateUnitCount}
            dateUnit={this.props.dateUnit}
            invalidStatuses={this.props.invalidStatuses}
            invalidPointLineType={this.props.invalidPointLineType}
            yAxisWidth={this.props.yAxisWidth}
            showCommonXLabel={this.props.showCommonXLabel}
            showFutureValue={this.props.showFutureValue}
            showTooltipOnPos={this.props.showTooltipOnPos}
            showDataLimitTitle={this.showDataLimitTitle}
            customizedMultipleYAxis={this.customizedMultipleYAxis}
            removeDataLimitsFromTooltip={this.removeDataLimitsFromTooltip}
          />
        </div>
        {this.props.xAxis.type === "DateTime" &&
          this.props.showDateTimePicker && (
            <div className="td-dr-root">
              <TrendDateRangePicker
                labelTexts={this.mergedLocaleText}
                isLeftButtonLocked={this.props.isLeftButtonLocked}
                isRightButtonLocked={this.props.isRightButtonLocked}
                dateUnit={this.props.dateUnit}
                dateUnits={this.props.dateUnits}
                dateUnitCount={this.props.dateUnitCount}
                onDateRangeChange={this.props.onDateRangeChange}
                startTime={this.props.xAxis.start as Date}
                endTime={this.props.xAxis.end as Date}
                tooltipFormat={this.props.tooltipFormat}
                d3Locale={this.props.d3Locale}
                minDate={this.props.minDate}
                maxDate={this.props.maxDate}
                datePickerFormat={this.props.datePickerFormat}
                datePickerOptions={this.props.datePickerOptions}
              />
            </div>
          )}
        {/* <p>lines</p> */}
        <hr className="td-hr-line"></hr>
        <br />
        {this.props.showTableData && (
          <>
            <hr className="td-hr-line"></hr>
            <div className="td-data-grid-cont">
              {this.showSeparatorInTrendTable === false && 
                  <Datagrid
                    columns={this.columns}
                    data={rowData}
                    rowHeight="small"
                    enableSorting={false}
                    enableColumnDragging={false}
                    zebraColoring={true}
                    borderStyle="none"
                    fitToContent={
                      this.showActiveDataLimitEyeIcon ? true : false
                    }                
                  />
              }
              {this.showSeparatorInTrendTable === true &&               
              <div id="parent-table">
                  {this.groupedData.map((group, index) => (
                    <div key={index} className={index !== 0 ? 'hide-thead' : ''}>
                      {index !== 0 && <hr className="td-hr-line"></hr>}
                      <Datagrid
                        columns={this.columns}
                        data={group}
                        rowHeight="small"
                        enableSorting={false}
                        enableColumnDragging={false}
                        zebraColoring={true}
                        borderStyle="none"
                        fitToContent={
                          this.props.showActiveDataLimitEyeIcon ? true : false
                        }
                      />
                    </div>))}
                </div>
                }
          </div>
          </>
        )}
      </div>
    );
  }
}
